import { Toggle } from "../Toggle";
import { t } from "../../i18n";
import {
  CircleCheck,
  CircleX,
  FlaskConical,
  LoaderCircle,
  Save,
  Trash2,
} from "lucide-react";
import type {
  AgentWorkspaceDefinition,
  AgentWorkspaceTestResult,
} from "@lumen/shared";
import type { Dispatch, SetStateAction } from "react";
import type { Draft } from "./AgentWorkspaceDraft";
import { isContainerWorkspaceType } from "./workspace-type";
import { WorkspacePolicyEditor } from "./WorkspacePolicyEditor";
import { ConnectedMachineWorkspaceEditor } from "./ConnectedMachineWorkspaceEditor";

type Props = {
  selected?: string;
  manageWorkspace: boolean;
  editable: boolean;
  draft: Draft;
  setDraft: Dispatch<SetStateAction<Draft>>;
  selectedRow?: AgentWorkspaceDefinition;
  canManagePlatform: boolean;
  canManageOrganization: boolean;
  testing: boolean;
  test: () => Promise<void>;
  saving: boolean;
  remove: () => Promise<void>;
  saveWorkspace: () => Promise<void>;
  testResult: AgentWorkspaceTestResult | null;
  error: string;
  siteOrganizationManagement: boolean;
  userFolder: string;
  setUserFolder: Dispatch<SetStateAction<string>>;
  userPolicy: Draft["commandPolicy"];
  setUserPolicy: Dispatch<SetStateAction<Draft["commandPolicy"]>>;
  userSshUsername: string;
  setUserSshUsername: Dispatch<SetStateAction<string>>;
  userAuthType: "password" | "key";
  setUserAuthType: Dispatch<SetStateAction<"password" | "key">>;
  userPassword: string;
  setUserPassword: Dispatch<SetStateAction<string>>;
  userKey: string;
  setUserKey: Dispatch<SetStateAction<string>>;
  userPassphrase: string;
  setUserPassphrase: Dispatch<SetStateAction<string>>;
  saveMine: () => Promise<void>;
  fetchingHostKeys: boolean;
  fetchHostKeys: () => Promise<void>;
  hostKeyCandidates: Array<{ algorithm: string; fingerprint: string }>;
  setHostKeyCandidates: Dispatch<
    SetStateAction<Array<{ algorithm: string; fingerprint: string }>>
  >;
  machineEnrollment: { token: string; serverUrl: string } | null;
  copyMachineEnrollment: (value: string, label: string) => void;
};

export function AgentWorkspaceEditor({
  selected,
  manageWorkspace,
  editable,
  draft,
  setDraft,
  selectedRow,
  canManagePlatform,
  canManageOrganization,
  testing,
  test,
  saving,
  remove,
  saveWorkspace,
  testResult,
  error,
  siteOrganizationManagement,
  userFolder,
  setUserFolder,
  userPolicy,
  setUserPolicy,
  userSshUsername,
  setUserSshUsername,
  userAuthType,
  setUserAuthType,
  userPassword,
  setUserPassword,
  userKey,
  setUserKey,
  userPassphrase,
  setUserPassphrase,
  saveMine,
  fetchingHostKeys,
  fetchHostKeys,
  hostKeyCandidates,
  setHostKeyCandidates,
  machineEnrollment,
  copyMachineEnrollment,
}: Props) {
  return (
    <div className="space-y-5">
      {!selected && !manageWorkspace ? (
        <div className="panel py-16 text-center text-sm text-stone-400">
          {t("Select a workspace to configure your access.")}
        </div>
      ) : (
        <div className="panel space-y-4 p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <span className="label">{t("Name")}</span>
              <input
                className="field"
                disabled={!editable}
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </div>
            <div>
              <span className="label">{t("Slug")}</span>
              <input
                className="field font-mono"
                disabled={!editable}
                placeholder={t("node-development")}
                value={draft.slug}
                onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
              />
            </div>
            <label>
              <span className="label">{t("Type")}</span>
              <select
                className="field"
                disabled={!editable || Boolean(selected)}
                value={draft.type}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    type: e.target.value as Draft["type"],
                  })
                }
              >
                <option value="container">{t("Container image")}</option>
                <option value="process-sandbox">{t("Process sandbox")}</option>
                <option value="rootless-podman">{t("Rootless Podman")}</option>
                <option value="ssh">{t("SSH connection")}</option>
                <option value="proxmox">{t("Proxmox cluster")}</option>
                <option value="machine">{t("Connected machine")}</option>
              </select>
            </label>
          </div>
          {isContainerWorkspaceType(draft.type) && (
            <label>
              <span className="label">{t("Workspace persistence")}</span>
              <select
                className="field"
                disabled={!editable || Boolean(selected)}
                value={draft.workspaceMode}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    workspaceMode: e.target.value as Draft["workspaceMode"],
                  })
                }
              >
                <option value="isolated">
                  {t("Isolated per conversation")}
                </option>
                <option value="machine">{t("Shared machine workspace")}</option>
              </select>
              <p className="mt-1 text-[11px] leading-5 text-stone-400">
                {draft.workspaceMode === "machine"
                  ? t(
                      "Your conversations using this workspace share files and Git state. Concurrent edits may affect the same files.",
                    )
                  : t(
                      "Each conversation gets its own isolated workspace and Git checkout.",
                    )}
              </p>
              {selected && (
                <p className="mt-1 text-[11px] leading-5 text-stone-400">
                  {t(
                    "Workspace persistence is set when a workspace is created and cannot be changed.",
                  )}
                </p>
              )}
            </label>
          )}
          <label>
            <span className="label">{t("Description")}</span>
            <input
              className="field"
              disabled={!editable}
              value={draft.description}
              onChange={(e) =>
                setDraft({ ...draft, description: e.target.value })
              }
            />
          </label>
          {isContainerWorkspaceType(draft.type) ? (
            <>
              <label>
                <span className="label">{t("OCI image")}</span>
                <input
                  className="field font-mono"
                  disabled={!editable}
                  placeholder="alpine:latest"
                  value={draft.image}
                  onChange={(e) =>
                    setDraft({ ...draft, image: e.target.value })
                  }
                />
              </label>
              <label>
                <span className="label">
                  {t("Setup instructions")}
                  <span className="font-normal text-stone-400">
                    {t("(optional)")}
                  </span>
                </span>
                <textarea
                  className="field min-h-24 resize-y font-mono text-xs leading-5"
                  disabled={!editable}
                  rows={6}
                  placeholder={t(
                    "Shell commands run once when the container starts, e.g. to install a toolchain",
                  )}
                  value={draft.setupInstructions}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      setupInstructions: e.target.value,
                    })
                  }
                />
                <p className="text-[11px] leading-5 text-stone-400">
                  {t(
                    "Runs on first start and again after the instructions change. Installed tools persist for the workspace lifetime.",
                  )}
                </p>
              </label>
              <div className="grid gap-4 sm:grid-cols-3">
                <label>
                  <span className="label">{t("CPU limit")}</span>
                  <input
                    type="number"
                    className="field"
                    disabled={!editable}
                    value={draft.cpuLimit}
                    onChange={(e) =>
                      setDraft({ ...draft, cpuLimit: e.target.value })
                    }
                  />
                </label>
                <label>
                  <span className="label">{t("Memory MB")}</span>
                  <input
                    type="number"
                    className="field"
                    disabled={!editable}
                    value={draft.memoryLimitMb}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        memoryLimitMb: e.target.value,
                      })
                    }
                  />
                </label>
                <label className="flex items-end gap-2 pb-3 text-sm">
                  <Toggle
                    disabled={!editable}
                    checked={draft.networkEnabled}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        networkEnabled: e.target.checked,
                      })
                    }
                  />{" "}
                  {t("Network access")}
                </label>
              </div>
            </>
          ) : draft.type === "machine" ? (
            <ConnectedMachineWorkspaceEditor
              enrollment={machineEnrollment}
              machineBusy={null}
              onCopy={copyMachineEnrollment}
            />
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-[1fr_110px]">
                <label>
                  <span className="label">{t("SSH host")}</span>
                  <input
                    className="field"
                    disabled={!editable}
                    value={draft.sshHost}
                    onChange={(e) =>
                      setDraft({ ...draft, sshHost: e.target.value })
                    }
                  />
                </label>
                <label>
                  <span className="label">{t("Port")}</span>
                  <input
                    type="number"
                    className="field"
                    disabled={!editable}
                    value={draft.sshPort}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        sshPort: Number(e.target.value),
                      })
                    }
                  />
                </label>
              </div>
              <div className="rounded-xl border border-stone-200 p-4 dark:border-white/10">
                <div className="flex flex-wrap items-end gap-3">
                  <label className="min-w-0 flex-1">
                    <span className="label">{t("Host-key fingerprint")}</span>
                    <input
                      className="field font-mono text-xs"
                      disabled={!editable}
                      placeholder="SHA256:…"
                      value={draft.sshHostKeyFingerprint}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          sshHostKeyFingerprint: e.target.value,
                        })
                      }
                    />
                  </label>
                  <button
                    type="button"
                    className="button mb-0.5"
                    disabled={!editable || !draft.sshHost || fetchingHostKeys}
                    onClick={() => void fetchHostKeys()}
                  >
                    {fetchingHostKeys ? (
                      <LoaderCircle className="animate-spin" size={15} />
                    ) : (
                      <FlaskConical size={15} />
                    )}{" "}
                    {t("Fetch host keys")}
                  </button>
                </div>
                {draft.sshHostKeyAlgorithm && (
                  <p className="mt-2 text-[11px] text-stone-400">
                    {t("Approved algorithm:")}
                    <code>{draft.sshHostKeyAlgorithm}</code>
                  </p>
                )}
                {hostKeyCandidates.length > 0 && (
                  <div className="mt-4 space-y-2 border-t border-stone-100 pt-4 dark:border-white/10">
                    <p className="text-xs font-semibold">
                      {t(
                        "The server presented these host keys. Verify one through a trusted channel before accepting it.",
                      )}
                    </p>
                    {hostKeyCandidates.map((candidate) => (
                      <div
                        key={`${candidate.algorithm}-${candidate.fingerprint}`}
                        className="flex flex-wrap items-center gap-2 rounded-lg bg-stone-50 p-3 text-xs dark:bg-white/5"
                      >
                        <code className="font-semibold">
                          {candidate.algorithm}
                        </code>
                        <code className="min-w-0 flex-1 break-all text-stone-500">
                          {candidate.fingerprint}
                        </code>
                        <button
                          type="button"
                          className="button text-xs"
                          onClick={() => {
                            setDraft({
                              ...draft,
                              sshHostKeyFingerprint: candidate.fingerprint,
                              sshHostKeyAlgorithm: candidate.algorithm,
                            });
                            setHostKeyCandidates([]);
                          }}
                        >
                          {t("I have verified this key")}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <label>
                  <span className="label">{t("Credential ownership")}</span>
                  <select
                    className="field"
                    disabled={!editable || draft.type === "proxmox"}
                    value={draft.authScope}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        authScope: e.target.value as Draft["authScope"],
                      })
                    }
                  >
                    <option value="global">
                      {t("One workspace credential")}
                    </option>
                    <option value="user">{t("Each user chooses")}</option>
                  </select>
                </label>
                {draft.authScope === "global" && (
                  <label>
                    <span className="label">{t("Authentication")}</span>
                    <select
                      className="field"
                      disabled={!editable}
                      value={draft.globalAuthType}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          globalAuthType: e.target
                            .value as Draft["globalAuthType"],
                        })
                      }
                    >
                      <option value="key">{t("SSH key")}</option>
                      <option value="password">{t("Password")}</option>
                    </select>
                  </label>
                )}
              </div>
              {draft.authScope === "global" && (
                <label>
                  <span className="label">{t("Global SSH username")}</span>
                  <input
                    className="field"
                    disabled={!editable}
                    value={draft.sshUsername}
                    onChange={(e) =>
                      setDraft({ ...draft, sshUsername: e.target.value })
                    }
                  />
                </label>
              )}
              {editable &&
                draft.authScope === "global" &&
                (draft.globalAuthType === "password" ? (
                  <label>
                    <span className="label">{t("Password")}</span>
                    <input
                      type="password"
                      className="field"
                      placeholder={
                        selectedRow?.globalCredentialConfigured
                          ? t("Leave blank to keep existing password")
                          : ""
                      }
                      value={draft.password}
                      onChange={(e) =>
                        setDraft({ ...draft, password: e.target.value })
                      }
                    />
                  </label>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label>
                      <span className="label">{t("Private key")}</span>
                      <textarea
                        className="field min-h-28 font-mono text-xs"
                        placeholder={
                          selectedRow?.globalCredentialConfigured
                            ? t("Leave blank to keep existing key")
                            : t("OpenSSH private key")
                        }
                        value={draft.privateKey}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            privateKey: e.target.value,
                          })
                        }
                      />
                    </label>
                    <label>
                      <span className="label">{t("Key passphrase")}</span>
                      <input
                        type="password"
                        className="field"
                        value={draft.passphrase}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            passphrase: e.target.value,
                          })
                        }
                      />
                    </label>
                  </div>
                ))}
            </>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <label>
              <span className="label">
                {t("Starting folder")}
                <span className="font-normal text-stone-400">
                  {t("(optional)")}
                </span>
              </span>
              <input
                className="field font-mono"
                disabled={!editable}
                placeholder={
                  isContainerWorkspaceType(draft.type)
                    ? t("Use the workspace working directory")
                    : t("Use the SSH login directory")
                }
                value={draft.startingFolder}
                onChange={(e) =>
                  setDraft({ ...draft, startingFolder: e.target.value })
                }
              />
            </label>
            <div>
              <span className="label">{t("Maximum command time")}</span>
              <input
                type="number"
                className="field"
                disabled={!editable || draft.commandTimeoutSeconds === null}
                min={1}
                max={86400}
                value={draft.commandTimeoutSeconds ?? ""}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    commandTimeoutSeconds: e.target.value
                      ? Number(e.target.value)
                      : null,
                  })
                }
              />
              <label className="mt-2 flex items-center gap-2 text-xs text-stone-500 dark:text-stone-400">
                <Toggle
                  disabled={!editable}
                  checked={draft.commandTimeoutSeconds === null}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      commandTimeoutSeconds: e.target.checked ? null : 120,
                    })
                  }
                />
                {t("No time limit")}
              </label>
            </div>
          </div>
          <div>
            <span className="label">{t("Workspace command policy")}</span>
            <WorkspacePolicyEditor
              value={draft.commandPolicy}
              onChange={(commandPolicy) =>
                setDraft({ ...draft, commandPolicy })
              }
              requireFallback
              disabled={!editable}
            />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <Toggle
              disabled={!editable}
              className="mt-1"
              checked={draft.allowUserPolicyRelaxation}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  allowUserPolicyRelaxation: e.target.checked,
                })
              }
            />
            <span>
              <strong>{t("Users may weaken workspace rules")}</strong>
              <span className="block text-xs leading-5 text-stone-400">
                {t(
                  "When disabled, user overrides can only make commands stricter.",
                )}
              </span>
            </span>
          </label>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <Toggle
                disabled={!editable}
                checked={draft.enabled}
                onChange={(e) =>
                  setDraft({ ...draft, enabled: e.target.checked })
                }
              />{" "}
              {t("Available in chat")}
            </label>
            {editable && (
              <label className="flex items-center gap-2 text-sm">
                <span>{t("Visibility")}</span>
                <select
                  className="field w-auto py-2"
                  value={draft.scope}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      scope: e.target.value as Draft["scope"],
                    })
                  }
                >
                  <option value="personal">{t("Only me")}</option>
                  {canManageOrganization && (
                    <option value="organization">
                      {t("This organization")}
                    </option>
                  )}
                  {canManagePlatform && (
                    <option value="platform">{t("Global")}</option>
                  )}
                </select>
              </label>
            )}
            <div className="ml-auto flex gap-2">
              {selected && (
                <button
                  className="button"
                  disabled={testing}
                  onClick={() => void test()}
                >
                  <FlaskConical size={15} />
                  {testing ? "Testing…" : "Test"}
                </button>
              )}
              {selectedRow?.deletable && (
                <button
                  className="button text-red-600"
                  disabled={saving}
                  onClick={() => void remove()}
                >
                  <Trash2 size={15} /> {t("Delete")}
                </button>
              )}
              {editable && (
                <button
                  className="button-primary"
                  disabled={saving || !draft.name}
                  onClick={() => void saveWorkspace()}
                >
                  <Save size={15} /> {t("Save workspace")}
                </button>
              )}
            </div>
          </div>
          {testResult && (
            <div
              className={`flex gap-2 rounded-xl p-3 text-sm ${testResult.ok ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300" : "bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300"}`}
            >
              {testResult.ok ? (
                <CircleCheck size={17} />
              ) : (
                <CircleX size={17} />
              )}
              <span>
                {testResult.message}
                {testResult.shell ? ` Shell: ${testResult.shell}` : ""}
              </span>
            </div>
          )}
          {error && (
            <pre className="rounded-xl bg-red-50 p-3 text-xs whitespace-pre-wrap text-red-700 dark:bg-red-950/30 dark:text-red-300">
              {error}
            </pre>
          )}
        </div>
      )}
      {selectedRow && !siteOrganizationManagement && (
        <div className="panel space-y-4 p-5">
          <div>
            <h2 className="font-bold">{t("My workspace settings")}</h2>
            <p className="mt-1 text-xs text-stone-400">
              {t("These values apply only to your commands and credentials.")}
            </p>
          </div>
          <label>
            <span className="label">
              {t("Starting folder override")}
              <span className="font-normal text-stone-400">
                {t("(optional)")}
              </span>
            </span>
            <input
              className="field font-mono"
              placeholder={
                selectedRow.startingFolder
                  ? `Use workspace setting: ${selectedRow.startingFolder}`
                  : t("Use the environment default")
              }
              value={userFolder}
              onChange={(e) => setUserFolder(e.target.value)}
            />
          </label>
          <div>
            <span className="label">{t("My command-policy overrides")}</span>
            <WorkspacePolicyEditor
              value={userPolicy}
              onChange={setUserPolicy}
            />
          </div>
          {selectedRow.type === "ssh" && selectedRow.authScope === "user" && (
            <>
              <label>
                <span className="label">{t("My SSH username")}</span>
                <input
                  className="field"
                  value={userSshUsername}
                  onChange={(e) => setUserSshUsername(e.target.value)}
                />
              </label>
              <label>
                <span className="label">{t("My authentication method")}</span>
                <select
                  className="field"
                  value={userAuthType}
                  onChange={(e) =>
                    setUserAuthType(e.target.value as typeof userAuthType)
                  }
                >
                  <option value="key">{t("SSH key")}</option>
                  <option value="password">{t("Password")}</option>
                </select>
              </label>
              {userAuthType === "password" ? (
                <label>
                  <span className="label">{t("My password")}</span>
                  <input
                    type="password"
                    className="field"
                    placeholder={
                      selectedRow.userCredentialConfigured
                        ? t("Leave blank to keep existing password")
                        : ""
                    }
                    value={userPassword}
                    onChange={(e) => setUserPassword(e.target.value)}
                  />
                </label>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  <label>
                    <span className="label">{t("My private key")}</span>
                    <textarea
                      className="field min-h-28 font-mono text-xs"
                      placeholder={
                        selectedRow.userCredentialConfigured
                          ? t("Leave blank to keep existing key")
                          : t("OpenSSH private key")
                      }
                      value={userKey}
                      onChange={(e) => setUserKey(e.target.value)}
                    />
                  </label>
                  <label>
                    <span className="label">{t("Passphrase")}</span>
                    <input
                      type="password"
                      className="field"
                      value={userPassphrase}
                      onChange={(e) => setUserPassphrase(e.target.value)}
                    />
                  </label>
                </div>
              )}
            </>
          )}
          <div className="flex justify-end">
            <button
              className="button-primary"
              disabled={saving}
              onClick={() => void saveMine()}
            >
              <Save size={15} /> {t("Save my settings")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
